import { debounce } from "lodash";
import { useTranslation } from "next-i18next";
import { SyntheticEvent, useState, ChangeEvent, useEffect } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import Link from "next/link";
import { useRouter } from "next/router";
import LinkComponent from "@Components/Link";
import ShopByContainer from "@Components/menuBar/ShopByContainer";
import ShopPopOut from "@Components/shop-pop-out/ShopPopOut";
import { Button, Select } from "@Components/ui";
import ZipCodePopUp from "@Components/zipCodePopUp/ZipCodePopUp";
import { ACCOUNT_MENU } from "@Constants/common";
import { regionOptions } from "@Constants/countryRegion";
import { SCROLL_DEBOUNCE_DELAY } from "@Constants/debounceDelay";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import linkListClasses from "@Styles/snippets/_linklist--main-nav.module.scss";
import { NotificationBarProps } from "@Types/NotificationBar";
import { ZipCodeBarProps } from "@Types/ZipCodeBar";
import { ZipCodePopUpsProps } from "@Types/ZipCodePopUps";
import { getItem } from "@Utils/browserStorage";
import { currency } from "@Utils/index";
import { isCanadianLocale } from "@Utils/locale";
import Icon from "@wff/components/ui/icon";
import { useCustomer } from "@wff/hooks/useCustomer";
import { useLocaleRouter } from "@wff/hooks/useLocaleRouter";
import { topNavMenuProps } from "@wff/interfaces";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { ZipCodeSelector, toggleZipCodePopUp } from "@wff/store/zipcodeSlice";
import Image from "components/Image";
import { cartDrawerActions, cartDrawerSelector } from "store/cartDrawerSlice";
import { cartQuantitySelector } from "store/cartSlice";
import { localeSelector } from "store/localeSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { AccountMenu } from "./AccountMenu";
import { NavLink } from "./NavLink";
import { SearchInput } from "./SearchInput";
import CartDrawer from "./cart-drawer/CartDrawer";
import CartToast from "./cart-toast/CartToast";
import L2MenuLinks from "./menu-links/L2MenuLinks";
import { MenuLinks } from "./menu-links/MenuLinks";
import NotificationBar from "./notificationBar/NotificationBar";
import ZipCodeBar from "./zipCodeBar/ZipCodeBar";
import classes from "./Header.module.scss";
import navLinkClasses from "./NavLink.module.scss";

const MINIMUM_SCROLL_DIFFERENCE = 200;
const OFFSET = 2;

interface HeaderProps {
  siteLogo?: {
    url?: string;
  };
  topNavMenus?: topNavMenuProps;
  joinText?: string;
  profileImage?: {
    url?: string;
  };
  cartImage?: {
    url?: string;
  };
  shopByNavigation: {
    items: {
      title: string;
      link: string;
      icon: {
        url: string;
      };
    }[];
  };
  notificationBar: NotificationBarProps;
  postalCodeBar: ZipCodeBarProps;
  postalCodePopUps: ZipCodePopUpsProps;
  showTopNavMenusItemBecomeMemberForCA?: boolean;
  showUnlockFreeDelBtnForCA?: boolean;
  pageTitle: string;
  productSKU: string;
}

//eslint-disable-next-line sonarjs/cognitive-complexity
export function Header({
  //siteLogo, revert back this after the quality site logo uploaded to the CTFul
  topNavMenus,
  joinText,
  profileImage,
  cartImage,
  shopByNavigation,
  notificationBar,
  postalCodeBar,
  postalCodePopUps,
  showTopNavMenusItemBecomeMemberForCA,
  showUnlockFreeDelBtnForCA,
  pageTitle,
  productSKU,
}: HeaderProps) {
  const { push, asPath } = useLocaleRouter();
  const cartQuantity = useAppSelector(cartQuantitySelector);
  const currentLocale = useAppSelector(localeSelector);
  const { zipcode, availability } = useAppSelector(ZipCodeSelector);
  const cartSlice = useAppSelector(cartSliceSelector);
  const { isLoggedIn } = useCustomer();
  const isCanadianUser = isCanadianLocale(currentLocale);

  const subTotal = isCanadianLocale(currentLocale.languageCode)
    ? cartSlice?.rawSubTotal
    : cartSlice?.subTotal;

  const becomeMemberPageURL = `/${currentLocale.languageCode}/become-a-member`;
  let topNavMenusItems = topNavMenus?.items;

  if (!showTopNavMenusItemBecomeMemberForCA) {
    topNavMenusItems = topNavMenus?.items?.filter(
      (item) => item?.title !== ACCOUNT_MENU.ITEM.BECOME_A_MEMBER
    );
  }

  const [isOpen, setIsOpen] = useState(false);
  const [shopPopOutData, setShopPopOutData] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(
    currentLocale?.languageCode
  );
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const { isVisible } = useAppSelector(cartDrawerSelector);

  const isDesktop = useMediaQuery("(min-width:60em)");
  const isMobile = useMediaQuery("(max-width:48em)");
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslation();

  const [isPDP, setIsPDP] = useState(false);
  const zipCodeAvailability = !zipcode && isCanadianUser;
  const { query } = router;
  const searchQuery = query?.q ? (query?.q as string) : "";
  const minimumScrollDistanceFromTop = isMobile ? 500 : 260;
  const navClassDesktop = !isMobile
    ? isVisible
      ? classes["site-header__nav-show-desktop"]
      : classes["site-header__nav-hide-desktop"]
    : "";
  const navClassIndexMobile =
    isMobile && !isNavExpanded && !searchQuery
      ? !isVisible
        ? classes["site-header__nav-hide-index-mobile"]
        : ""
      : "";
  const navClassMobileTicker =
    isMobile && !isNavExpanded && !searchQuery
      ? isVisible
        ? classes["site-header__nav-show-ticker-mobile"]
        : classes["site-header__nav-hide-ticker-mobile"]
      : "";
  const navClassMobileMain =
    isMobile && !isNavExpanded && !searchQuery
      ? isVisible
        ? classes["site-header__nav-show-main-mobile"]
        : classes["site-header__nav-hide-main-mobile"]
      : "";
  const navClassMobileSearch =
    isMobile && !isNavExpanded && !searchQuery
      ? isVisible
        ? classes["site-header__nav-show-search-mobile"]
        : classes["site-header__nav-hide-search-mobile"]
      : "";
  const navClassMobileZipcode =
    isMobile && !isNavExpanded && !searchQuery
      ? isVisible
        ? classes["site-header__nav-show-zipcode-mobile"]
        : classes["site-header__nav-hide-zipcode-mobile"]
      : "";
  const navClassMobileMenu =
    isMobile && !isNavExpanded && !searchQuery
      ? isVisible
        ? classes["site-header__nav-show-menu-mobile"]
        : classes["site-header__nav-hide-menu-mobile"]
      : "";

  const [l2CategoryData, setL2CategoryData] = useState({});
  const [l2CategoryDataLength, setL2CategoryDataLength] = useState(0);
  const validateZipCode = (event: SyntheticEvent) => {
    if (zipCodeAvailability) {
      event.preventDefault();
      dispatch(toggleZipCodePopUp());
    }
  };

  const toggleCart = (event: SyntheticEvent) => {
    event.preventDefault();
    if (zipCodeAvailability) dispatch(toggleZipCodePopUp());
    else dispatch(cartDrawerActions.toggleCartDrawer());
  };

  const handleClose = () => {
    setIsOpen(false);
    setShopPopOutData([]);
    setIsNavExpanded(false);
    setL2CategoryData({});
    setL2CategoryDataLength(0);
  };

  const handleRegionSelect = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newRoute = asPath.replace(
      currentLocale?.languageCode,
      e?.target?.value
    );
    setSelectedRegion(e?.target?.value);
    push(newRoute, {
      skipLocale: true,
    });
  };

  const handleScroll = debounce(() => {
    const currentScrollPos = window.pageYOffset;
    const isBottomOfPage =
      window.innerHeight + currentScrollPos >=
      document.body.offsetHeight - OFFSET;

    const isListVisible =
      (prevScrollPos > currentScrollPos &&
        prevScrollPos - currentScrollPos > MINIMUM_SCROLL_DIFFERENCE) ||
      currentScrollPos < minimumScrollDistanceFromTop ||
      (isBottomOfPage && isMobile)
        ? true
        : prevScrollPos < currentScrollPos &&
          prevScrollPos - currentScrollPos < MINIMUM_SCROLL_DIFFERENCE
        ? false
        : true;

    dispatch(cartDrawerActions.setHeaderPosition(isListVisible));
    setPrevScrollPos(currentScrollPos);
  }, SCROLL_DEBOUNCE_DELAY);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos, isVisible, handleScroll]);

  //CA-ONLY
  //Redirected to the login page
  const redirectToLoginPage = () => {
    router.push("/en-CA/account/login/");
  };

  const isZipCodeBarPresent_CA =
    isCanadianUser && zipcode && availability !== null;

  const handleChildData = (childData: any) => {
    setL2CategoryData(childData);
    setL2CategoryDataLength(Object.keys(childData).length);
  };

  const handleBackFromL2 = () => {
    setL2CategoryData({});
    setL2CategoryDataLength(0);
  };

  useEffect(() => {
    setIsPDP(router?.pathname?.split("/")[2] === "products");
  }, [router]);

  const renderIfIsMobileAndNavExpanded = (item: any) => {
    return (
      <div>
        <AccountMenu
          joinText={joinText}
          profileImage={profileImage}
          isNavExpanded={isNavExpanded}
          onAccountNavLinkClick={handleClose}
        />
        <a
          className={`${navLinkClasses["nav-link"]}`}
          style={{ cursor: "pointer" }}
        >
          <span className={`${linkListClasses["main-nav__link-level1-hover"]}`}>
            <h3>{item?.title}</h3>
          </span>
        </a>

        <MenuLinks
          menuLinks={item?.menus?.items[0]?.categorySubNavs?.items}
          onLinkClick={handleClose}
          onDataReceived={handleChildData}
        />
      </div>
    );
  };

  const renderIfNotIsMobileAndNotNavExpanded = (item: any) => (
    <div className={classes["site-header__each"]} key={item?.title}>
      <a
        className={`${navLinkClasses["nav-link"]}`}
        style={{ cursor: "pointer" }}
      >
        <span
          className={`${linkListClasses["main-nav__link-level1-hover"]}`}
          onClick={() => {
            setIsOpen(!isOpen);
            setShopPopOutData(!isOpen ? item?.menus?.items : []);
          }}
        >
          {isNavExpanded && isMobile && (
            <div>
              <h3>{item?.title}</h3>
            </div>
          )}
          {!isNavExpanded || !isMobile ? item?.title : null}
        </span>
      </a>
    </div>
  );

  // Extracting the nested ternary operations
  const headerMainClass = `${classes["site-header__main"]} `;
  const expandedClass = classes["expanded"];
  const indexClass =
    isMobile && !searchQuery ? classes["site-header__main-index"] : "";
  const mobileMainClass = navClassMobileMain;

  // Combining the extracted classes using string interpolation
  const combinedClasses = isNavExpanded
    ? `${headerMainClass} ${expandedClass}`
    : `${headerMainClass} ${indexClass} ${mobileMainClass}`;

  const baseClasses = isNavExpanded
    ? `${classes["site-header__items"]} ${classes["expanded"]}`
    : classes["site-header__items"];

  const shouldShowAccountMenu =
    isZipCodeBarPresent_CA && l2CategoryDataLength === 0;
  const accountMenuClasses = shouldShowAccountMenu
    ? classes["site-header__account-menu"]
    : "";

  const zipCodeAvailabilityInLocalStorageForCA = getItem(
    STORAGE_KEYS.CUSTOMER_ZIP_AVAILABILITY_CA,
    STORAGE_TYPES.LOCAL
  );
  const isDeliveryAvailable =
    availability || zipCodeAvailabilityInLocalStorageForCA === "true";
  return (
    <>
      <header
        className={`${classes["site-header"]} ${navClassIndexMobile}`}
        id="site-header"
      >
        {((!isMobile && notificationBar) ||
          (isMobile &&
            !isNavExpanded &&
            !searchQuery &&
            notificationBar &&
            !isPDP)) && (
          <div className={navClassMobileTicker}>
            <NotificationBar {...notificationBar} />
          </div>
        )}
        <nav className={combinedClasses}>
          <button
            data-testid="hamburger"
            className={
              isNavExpanded
                ? `${classes["site-header__hamburger"]} ${classes["expanded"]}`
                : classes["site-header__hamburger"]
            }
            onClick={() => {
              setIsNavExpanded(!isNavExpanded);
              setL2CategoryData({});
              setL2CategoryDataLength(0);
            }}
          >
            <Icon iconName={!isNavExpanded ? "hamburger" : "x-hamburger"} />
          </button>

          <Link href={{ pathname: "/" }}>
            <a className={classes["site-header__logo"]}>
              <Image
                src="/WILD_FORK_LOGO.png"
                alt="Logo"
                layout="fill"
                objectFit="contain"
              />
            </a>
          </Link>
          <div
            className={`${baseClasses} ${accountMenuClasses}`}
            style={{
              paddingTop: isMobile && isNavExpanded ? "0rem" : "",
            }}
          >
            {topNavMenus && l2CategoryDataLength === 0 ? (
              topNavMenusItems?.map((item: any, index: number) => {
                const hasItems = item?.menus?.items.length > 0;
                return hasItems ? (
                  isMobile && isNavExpanded ? (
                    renderIfIsMobileAndNavExpanded(item)
                  ) : (
                    renderIfNotIsMobileAndNotNavExpanded(item)
                  )
                ) : (
                  <div
                    className={classes["site-header__each"]}
                    key={`${item.title + index}`}
                  >
                    <NavLink key={item?.link} href={`${item?.link}`}>
                      <span
                        className={`${linkListClasses["main-nav__link-level1-hover"]}`}
                      >
                        {isNavExpanded && isMobile ? (
                          <div>
                            <h3 data-testid={item.title}>{item?.title}</h3>
                          </div>
                        ) : (
                          item?.title
                        )}
                      </span>
                    </NavLink>
                  </div>
                );
              })
            ) : (
              <L2MenuLinks
                l2CategoryData={l2CategoryData}
                backFromL2={handleBackFromL2}
                handleClose={handleClose}
              />
            )}

            {/* Login and Logout functionality for Mobile users: ONLY FOR CA*/}
            {isCanadianUser &&
            isNavExpanded &&
            isMobile &&
            !isLoggedIn &&
            l2CategoryDataLength === 0 ? (
              <div className={classes["site-header__each"]}>
                <NavLink>
                  <span
                    className={`${linkListClasses["main-nav__link-level1-hover"]}`}
                  >
                    <div onClick={() => redirectToLoginPage()}>
                      <h3 data-testid={"test-id-login"}>
                        {t(ACCOUNT_MENU.NAV_ITEM.LOGIN)}
                      </h3>
                    </div>
                  </span>
                </NavLink>
              </div>
            ) : (
              <></>
            )}
          </div>

          {!isMobile && <SearchInput />}

          <div
            className={
              isNavExpanded
                ? `${classes["site-header__itemstwo"]} ${classes["expanded"]}`
                : classes["site-header__itemstwo"]
            }
          >
            <div>
              <Select
                options={regionOptions}
                selected={selectedRegion}
                onChange={handleRegionSelect}
                className={classes["region"]}
              />
              {currentLocale?.languageCode === "en-US" && (
                <div className={classes["site-header__each"]}>
                  <NavLink href="/find-a-store">
                    <Image
                      src="data:image/png;base64,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"
                      width={18}
                      height={18}
                      alt="store"
                    />
                    Store
                  </NavLink>
                </div>
              )}
            </div>

            {!isMobile && (
              <AccountMenu
                joinText={joinText}
                profileImage={profileImage}
                isNavExpanded={isNavExpanded}
                onAccountNavLinkClick={handleClose}
              />
            )}
          </div>

          {showUnlockFreeDelBtnForCA && isMobile && !isPDP && (
            <LinkComponent href={becomeMemberPageURL}>
              <Button
                className={`${classes["unlock-del-btn--mob"]} ${
                  !isNavExpanded && !searchQuery
                    ? classes["unlock-del-btn--mob-index"]
                    : ""
                } action action--primary-o`}
                type="button"
                data-testid="unlock-free-btn-mob"
              >
                {/* TODO from Nuwan: no translation yet for this. Replace the CTFul text after created */}
                BECOME
                <br />A MEMBER
              </Button>
            </LinkComponent>
          )}

          {!isNavExpanded ? (
            <Link href={`/${currentLocale.languageCode}/cart`} passHref>
              <div
                // Conditionally triggering the event and calling toggleCart function if user is canadian
                onClick={(e) => isCanadianUser && toggleCart(e)}
                className={classes["site-header__cart-subtotal"]}
              >
                {!isMobile ? (
                  <span
                    className={`${classes["site-header__cart-text"]}`}
                    onClick={validateZipCode}
                  >
                    {currency.format(currency.convertToDollar(subTotal))}
                  </span>
                ) : (
                  <></>
                )}
                <span
                  className={`${classes["site-header__main-item"]} ${classes["site-header__main-item--cart"]}`}
                  aria-label="Open Cart"
                  // Conditionally triggering the event and calling toggleCart function if user is not canadian
                  onClick={(e) => !isCanadianUser && toggleCart(e)}
                >
                  <Image
                    src={`${cartImage?.url}?w=30&h=30`}
                    width={30}
                    height={30}
                    alt="cart"
                    data-testid="cart"
                  />
                  <i
                    className={`${classes["site-header__main-item--cart-flame"]}`}
                    data-count={cartQuantity}
                  >
                    <Icon iconName="flame" />
                  </i>
                </span>
              </div>
            </Link>
          ) : (
            <></>
          )}
          {showUnlockFreeDelBtnForCA && !isMobile && (
            <LinkComponent
              href={becomeMemberPageURL}
              data-testid="free-delivery-button"
            >
              <Button
                className={`${classes["unlock-del-btn"]} action action--primary-o`}
                type="button"
                data-testid="unlock-free-btn"
              >
                {/* TODO from Nuwan: no translation yet for this. Replace the CTFul text after created */}
                BECOME
                <br />A MEMBER
              </Button>
            </LinkComponent>
          )}
        </nav>

        {isMobile && !isNavExpanded && (
          <div
            className={`${classes["site-header__search"]} ${navClassMobileSearch}`}
          >
            <SearchInput />
          </div>
        )}

        {(!isMobile ||
          (isMobile && !isNavExpanded && !searchQuery && !isPDP)) && (
          <div className={`${classes["site-header__nav"]} ${navClassDesktop}`}>
            <div className={navClassMobileZipcode}>
              <ZipCodeBar
                {...postalCodeBar}
                zipcode={zipcode}
                availability={availability}
              />
            </div>
            <div className={navClassMobileMenu}>
              <ShopByContainer shopByLinks={shopByNavigation?.items} />
            </div>
          </div>
        )}

        {isDesktop && (
          <ShopPopOut
            isOpen={isOpen}
            onClose={handleClose}
            menuData={shopPopOutData}
            currentLocale={currentLocale?.languageCode}
          />
        )}
      </header>
      {/* CartToast component */}
      <CartToast
        isDeliveryAvailable={isDeliveryAvailable}
        isStickyNavVisible={isVisible ? true : false}
      />
      <CartDrawer pageTitle={pageTitle} productSKU={productSKU} />
      <ZipCodePopUp {...postalCodePopUps} />
    </>
  );
}

Header.defaultProps = {
  siteLogo: {},
  topNavMenus: {},
  joinText: "",
  profileImage: {},
  cartImage: {},
  shopByNavigation: {
    items: [],
  },
};

export default Header;
